/**
 * Cadre : société (sidebar) + header Aurélia Factures + zone métier.
 */
import { useState, type ReactNode } from "react";
import { NAV } from "../shell/nav";
import { persistFxSidebarSlim, readFxSidebarSlim } from "../theme/sidebar";
import { ThemeMenu } from "../theme/ThemeMenu";
import type { SearchOpen } from "../features/search/types";
import type { AppPage } from "../types";
import { AppHeader } from "./AppHeader";
import { AureliaCopyright } from "./AureliaCopyright";
import { CompanySwitch } from "./CompanySwitch";
import { IconCollapse } from "./NavIcons";

type AppShellProps = {
  page: AppPage;
  onPage: (page: AppPage) => void;
  onSearchOpen?: (hit: SearchOpen) => void;
  children: ReactNode;
};

export function AppShell({ page, onPage, onSearchOpen, children }: AppShellProps) {
  const [slim, setSlim] = useState(readFxSidebarSlim);

  const toggleSlim = () => {
    setSlim((prev) => {
      const next = !prev;
      persistFxSidebarSlim(next);
      return next;
    });
  };

  return (
    <div className={`fx-app${slim ? " is-slim" : ""}`}>
      <aside className={`fx-aside${slim ? " is-slim" : ""}`}>
        <CompanySwitch slim={slim} onOpenSettings={() => onPage("settings")} />
        <nav className="fx-navlist" aria-label="Factures">
          {NAV.map((item) => {
            if (item.kind === "sep") {
              return <div key={item.id} className="fx-nav-sep" aria-hidden />;
            }
            const on = Boolean(item.ready && item.page && page === item.page);
            return (
              <button
                key={item.id}
                type="button"
                className={`fx-nav${on ? " is-on" : ""}`}
                disabled={!item.ready}
                title={item.ready ? item.hint : `${item.hint} — bientôt`}
                aria-current={on ? "page" : undefined}
                onClick={() => {
                  if (item.ready && item.page) onPage(item.page);
                }}
              >
                <span className="fx-nav__ico">
                  <item.Icon />
                </span>
                <span className="fx-nav__label">{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="fx-aside-foot">
          <div className="fx-aside-tools">
            <ThemeMenu />
            <button
              type="button"
              className="fx-iconbtn"
              aria-label={slim ? "Déplier le menu" : "Replier le menu"}
              title={slim ? "Déplier" : "Replier"}
              onClick={toggleSlim}
            >
              <IconCollapse slim={slim} />
            </button>
          </div>
          <AureliaCopyright />
        </div>
      </aside>
      <div className="fx-work">
        <AppHeader onOpen={onSearchOpen} />
        <main className="fx-main">{children}</main>
      </div>
    </div>
  );
}
